import React from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '@petmas/shared';
import { useCart } from '../../contexts/CartContext';
import { formatPrice, formatDiscount } from '../../utils/format';

interface ProductCardProps {
  product: Product;
  className?: string;
}

export function ProductCard({ product, className = '' }: ProductCardProps) {
  const { addItem } = useCart();

  const hasDiscount = product.discountPercent && product.discountPercent > 0;
  const isOutOfStock = product.variants?.every(v => v.stockAvailable <= 0) ?? false;

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Use first variant if available
    const variant = product.variants?.[0];
    if (!variant) return;

    addItem({
      productId: product.id,
      variantId: variant.id,
      productName: product.name,
      variantName: variant.description,
      price: product.price,
      imageUrl: product.imageUrl,
    });
  };

  return (
    <Link
      to={`/producto/${product.id}`}
      className={`product-card group flex flex-col ${className}`}
    >
      {/* Image with inset padding */}
      <div className="relative">
        <div className="image-wrapper aspect-square overflow-hidden">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <ImagePlaceholder className="w-16 h-16 text-gray-300" />
            </div>
          )}
        </div>

        {/* Badges */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {hasDiscount && (
            <span className="badge badge-discount">
              {formatDiscount(product.discountPercent!)}
            </span>
          )}
          {isOutOfStock && (
            <span className="badge badge-sold-out">Agotado</span>
          )}
        </div>

        {/* Quick add button - appears on hover */}
        {!isOutOfStock && (
          <button
            onClick={handleAddToCart}
            className="quick-add-btn"
            aria-label="Agregar al carrito"
          >
            <CartPlusIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        {/* Brand */}
        {product.brand && (
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">
            {product.brand}
          </p>
        )}

        {/* Name */}
        <h3 className="font-medium text-gray-800 line-clamp-2 flex-1 group-hover:text-primary transition-colors uppercase text-sm">
          {product.name}
        </h3>

        {/* Price - DM Serif Display font */}
        <div className="mt-3 flex items-baseline gap-2">
          <span className={`price ${hasDiscount ? 'price-discount' : ''}`}>
            {formatPrice(product.price)}
          </span>
          {hasDiscount && product.originalPrice && (
            <span className="price-original">
              {formatPrice(product.originalPrice)}
            </span>
          )}
        </div>

        {/* Add to cart button (mobile - always visible) */}
        {!isOutOfStock && (
          <button
            onClick={handleAddToCart}
            className="mt-4 btn btn-primary w-full sm:hidden"
          >
            <CartPlusIcon className="w-4 h-4 mr-2" />
            Agregar
          </button>
        )}

        {/* Out of stock message */}
        {isOutOfStock && (
          <p className="mt-4 text-sm text-gray-500 text-center py-2 bg-gray-100 rounded-lg">
            Sin stock disponible
          </p>
        )}
      </div>
    </Link>
  );
}

function ImagePlaceholder({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1}
        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function CartPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0zM12 9v3m0 0v3m0-3h3m-3 0H9"
      />
    </svg>
  );
}
